import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useActionHook } from "hooks";
import { useAccountSelector, useDealActions, useUserBlankTemplateIdSelector } from "store";
import {
  Characteristic,
  DealBase,
  DealStage,
  IAssetClass,
  ICharContact,
  IDealImportValidateResponse,
  ITemplate
} from "store/types";

type StageItem = Omit<DealStage, "deals" | "deals_count">;

// ============================================
// Types
// ============================================

export type DealCreateScreen = "choice" | "basic-form" | "characteristics" | "summary";

export type FlowPath = "manual" | "file-upload";

export interface LocalCharacteristicData {
  localId: string;
  characteristic_id: number;
  value: string;
  currency: string | null;
  percentage: number;
  is_default: boolean;
}

export interface DealCreateState {
  // Navigation
  currentScreen: DealCreateScreen;
  flowPath: FlowPath | null;
  screenHistory: DealCreateScreen[];

  // Basic Form fields
  dealName: string;
  stageId: number | null;
  assetClass: IAssetClass | null;
  subAssetClass: IAssetClass | null;
  templateId: number | null;
  isDebt: boolean;
  currency: string;
  geography: string;

  // Contact
  contacts: ICharContact[];

  // File upload
  file: File | null;
  validateResult: IDealImportValidateResponse | null;

  // More Info (Screen 4)
  characteristicsData: LocalCharacteristicData[];

  // Loading/error
  loading: boolean;
  error: string | null;
  fieldErrors: Record<string, string>;
  /** Server validation errors on contacts (deal_capital_seeker.<i>.<field>), keyed by contact id. */
  contactErrors: Record<string, string>;
}

const initialState: DealCreateState = {
  currentScreen: "basic-form",
  flowPath: null,
  screenHistory: ["basic-form"],
  dealName: "",
  stageId: null,
  assetClass: null,
  subAssetClass: null,
  templateId: null,
  isDebt: false,
  currency: "",
  geography: "",
  contacts: [],
  file: null,
  validateResult: null,
  characteristicsData: [],
  loading: false,
  error: null,
  fieldErrors: {},
  contactErrors: {}
};

// ============================================
// Context Value
// ============================================

interface DealCreateContextValue {
  state: DealCreateState;
  setState: React.Dispatch<React.SetStateAction<DealCreateState>>;
  setField: <K extends keyof DealCreateState>(key: K, value: DealCreateState[K]) => void;

  // Navigation
  goToScreen: (screen: DealCreateScreen) => void;
  goBack: () => void;

  // Reference data
  assetClasses: IAssetClass[];
  setAssetClasses: React.Dispatch<React.SetStateAction<IAssetClass[] | undefined>>;
  stages: StageItem[];
  templates: ITemplate[];
  setTemplates: React.Dispatch<React.SetStateAction<ITemplate[] | undefined>>;
  characteristics: Characteristic[];
  categories: string[];
  geographyCharacteristicId: number | null;
  referenceLoading: boolean;

  // Submission
  handleSubmit: () => Promise<void>;
  submitting: boolean;

  // Props
  onCancel: () => void;
}

const DealCreateContext = createContext<DealCreateContextValue | null>(null);

export const useDealCreateContext = (): DealCreateContextValue => {
  const context = useContext(DealCreateContext);
  if (!context) {
    throw new Error("useDealCreateContext must be used within DealCreateProvider");
  }
  return context;
};

// ============================================
// Provider
// ============================================

interface DealCreateProviderProps {
  children: React.ReactNode;
  stageId?: number;
  dealName?: string;
  status_id?: number;
  onCancel: () => void;
  onSubmit: (data: DealBase) => void;
}

export const DealCreateProvider: React.FC<DealCreateProviderProps> = ({
  children,
  stageId,
  dealName,
  onCancel,
  onSubmit
}) => {
  const blankTemplateId = useUserBlankTemplateIdSelector();
  const { allow_ai_deal_creation } = useAccountSelector() || {};

  const {
    createDealAction,
    getTemplatesAction,
    getAssetClassesAction,
    getDealStagesAction,
    getCharacteristicsGeneralDataAction
  } = useDealActions();

  // State
  const [state, setState] = useState<DealCreateState>(() => {
    return {
      ...initialState,
      ...(allow_ai_deal_creation && { currentScreen: "choice", screenHistory: ["choice"] }),
      ...(dealName && { dealName }),
      ...(stageId && { stageId }),
      ...(blankTemplateId && { templateId: blankTemplateId })
    };
  });

  // Reference data
  const {
    data: assetClasses = [],
    action: fetchAssetClasses,
    setData: setAssetClasses
  } = useActionHook(getAssetClassesAction);
  const { data: stages = [], action: fetchStages } = useActionHook(() =>
    getDealStagesAction<false>({ with_deals: false })
  );
  const { data: templates = [], action: fetchTemplates, setData: setTemplates } = useActionHook(getTemplatesAction);
  const {
    data: characteristicsGeneralData = { characteristic_list: [], categories: [] },
    action: fetchCharacteristicsGeneralData
  } = useActionHook(getCharacteristicsGeneralDataAction);
  const { categories, characteristic_list } = characteristicsGeneralData;
  const characteristics = useMemo(
    () => characteristic_list.filter((c) => c.name !== "Geography" && c.name !== "Currency"),
    [characteristic_list]
  );
  const geographyCharacteristicId = useMemo(
    () => characteristic_list.find((c) => c.name === "Geography")?.id ?? null,
    [characteristic_list]
  );
  const [referenceLoading, setReferenceLoading] = useState(true);

  // Submission state
  const [submitting, setSubmitting] = useState(false);

  // Load reference data on mount
  useEffect(() => {
    Promise.all([fetchAssetClasses(), fetchStages(), fetchTemplates(), fetchCharacteristicsGeneralData()]).finally(() =>
      setReferenceLoading(false)
    );
  }, []);

  // Set blank template once loaded
  useEffect(() => {
    if (blankTemplateId && !state.templateId) {
      setState((prev) => ({ ...prev, templateId: blankTemplateId }));
    }
  }, [blankTemplateId]);

  // Default deal stage to status_id === 1 for manual flow
  useEffect(() => {
    if (state.flowPath === "manual" && !state.stageId && !!stages.length) {
      const defaultStage = stages.find((s) => s.status_id === 1);
      if (defaultStage) {
        setState((prev) => (prev.stageId ? prev : { ...prev, stageId: defaultStage.id }));
      }
    }
  }, [state, stages]);

  // Navigation
  const goToScreen = useCallback((screen: DealCreateScreen) => {
    setState((prev) => ({
      ...prev,
      currentScreen: screen,
      screenHistory: prev.screenHistory.includes(screen) ? prev.screenHistory : [...prev.screenHistory, screen],
      error: null
    }));
  }, []);

  const goBack = useCallback(() => {
    setState((prev) => {
      const history = [...prev.screenHistory];
      const currentIndex = history.indexOf(prev.currentScreen);
      if (currentIndex <= 0) return prev;
      return {
        ...prev,
        currentScreen: history[currentIndex - 1],
        error: null
      };
    });
  }, []);

  const setField = useCallback(<K extends keyof DealCreateState>(key: K, value: DealCreateState[K]) => {
    setState((prev) => {
      const next: Partial<DealCreateState> = { [key]: value, error: null };
      if (Object.keys(prev.fieldErrors).length) {
        const { [key as string]: _, ...rest } = prev.fieldErrors;
        next.fieldErrors = rest;
      }
      return { ...prev, ...next };
    });
  }, []);

  // Submission
  const handleSubmit = useCallback(async () => {
    const { dealName: title, assetClass, subAssetClass, templateId, contacts, isDebt } = state;
    const effectiveTemplateId = templateId || blankTemplateId;

    if (!assetClass || !title.trim() || !effectiveTemplateId) {
      setState((prev) => ({ ...prev, error: "Please fill in all required fields" }));
      return;
    }

    setSubmitting(true);
    try {
      // All create-flow contacts are new; removed ones are dropped from the array. A numeric id means an
      // existing account contact (from the picker) → send it so the backend links instead of duplicating.
      const capitalSeekers = contacts.map((contact) => ({
        ...(typeof contact.id === "number" ? { id: contact.id } : {}),
        first_name: contact.first_name || "",
        last_name: contact.last_name || "",
        email: contact.email || "",
        firm: contact.firm || "",
        title: contact.title || "",
        phone_number: contact.phone_number || "",
        address: contact.address || "",
        role: contact.role ?? null,
        skill: contact.skill ?? null,
        asset_class: contact.asset_class ?? null
      }));
      const characteristicData = buildCharacteristicData(state);

      const { dealId, dealTitle } = await createDealAction({
        title: title.trim(),
        asset_type_id: 1,
        asset_class_id: assetClass.id,
        template_id: effectiveTemplateId,
        deal_capital_seeker: capitalSeekers,
        ...(subAssetClass && { sub_asset_class_id: subAssetClass.id }),
        ...(state.stageId && { deal_stage_id: state.stageId }),
        ...(isDebt && { type: "debt" }),
        ...(state.currency && { currency: state.currency }),
        ...(state.geography && { geography: state.geography }),
        ...(characteristicData.length && { characteristic_data: characteristicData }),
        ...(state.file && { file: state.file })
      });

      onSubmit({ id: dealId, title: dealTitle });
    } catch (err: unknown) {
      const error = err as Record<string, unknown>;
      const serverErrors = error?.errors as Record<string, string[]> | undefined;

      if (serverErrors && typeof serverErrors === "object") {
        const fieldErrors: Record<string, string> = {};
        // Per-contact errors arrive as `deal_capital_seeker.<index>.<field>` — collect them by index.
        const contactErrorByIndex: Record<number, string> = {};
        for (const [key, messages] of Object.entries(serverErrors)) {
          if (!Array.isArray(messages) || !messages.length) continue;
          const contactMatch = key.match(/^deal_capital_seeker\.(\d+)\./);
          if (contactMatch) {
            const idx = Number(contactMatch[1]);
            const msg = messages.join(", ").replace(/deal_capital_seeker\.\d+\./g, "");
            contactErrorByIndex[idx] = contactErrorByIndex[idx] ? `${contactErrorByIndex[idx]}, ${msg}` : msg;
          }
          fieldErrors[key] = messages.join(", ");
        }
        setState((prev) => {
          // Resolve each erroring contact index → its id so the card can be highlighted.
          const contactErrors: Record<string, string> = {};
          for (const [idxStr, msg] of Object.entries(contactErrorByIndex)) {
            const contact = prev.contacts[Number(idxStr)];
            if (contact?.id != null) contactErrors[String(contact.id)] = msg;
          }
          return { ...prev, fieldErrors, contactErrors };
        });
      } else {
        const errorMessage = (error?.message as string) || "Failed to create deal";
        setState((prev) => ({ ...prev, error: errorMessage }));
      }
    } finally {
      setSubmitting(false);
    }
  }, [state, createDealAction, onSubmit]);

  const filteredStages = useMemo(() => {
    return stages.filter((stage) => !stage.is_system);
  }, [stages]);

  const value = useMemo(
    () => ({
      state,
      setState,
      setField,
      goToScreen,
      goBack,
      assetClasses,
      setAssetClasses,
      stages: filteredStages,
      templates,
      setTemplates,
      characteristics,
      categories,
      geographyCharacteristicId,
      referenceLoading,
      handleSubmit,
      submitting,
      onCancel
    }),
    [
      state,
      assetClasses,
      setAssetClasses,
      filteredStages,
      templates,
      setTemplates,
      characteristics,
      categories,
      geographyCharacteristicId,
      referenceLoading,
      handleSubmit,
      submitting,
      goToScreen,
      goBack,
      setField,
      onCancel
    ]
  );

  return <DealCreateContext.Provider value={value}>{children}</DealCreateContext.Provider>;
};

// ============================================
// Helpers
// ============================================

function buildCharacteristicData(state: DealCreateState) {
  return state.characteristicsData
    .filter((cd) => cd.value)
    .map((cd, index) => ({
      id: `new_${index}`,
      deal_id: 0,
      characteristic_id: cd.characteristic_id,
      value: cd.value,
      currency: cd.currency,
      is_default: cd.is_default,
      percentage: cd.percentage,
      is_deleted: false,
      investor_id: null,
      round_id: null
    }));
}
